import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { PetaniDataType } from '../daftar-petani/types'
import type { AlsintanDataType } from '../daftar-poktan/types'
import type {
  GapoktanAnggotaDataType,
  GapoktanDataType,
  GapoktanStrukturalDataType,
  RiwayatKodeGapoktanDataType
} from './types'
import type {
  AddAlsintanValues,
  AddAnggotaGapoktanValues,
  AddStrukturalGapoktanValues,
  FilterGapoktanValues,
  MutationGapoktanValues
} from './validation'

const GAPOKTAN_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['gapoktan', key] as const

const getAll = (
  params?: FilterGapoktanValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<GapoktanDataType>>> => ({
  queryKey: GAPOKTAN_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<GapoktanDataType>>
      >('/kelembagaan/gapoktan', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<GapoktanDataType>> => ({
  queryKey: GAPOKTAN_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<GapoktanDataType>>(
        `/kelembagaan/gapoktan/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllAnggotaGapoktan = (
  params?: FilterGapoktanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<GapoktanAnggotaDataType>>
> => ({
  queryKey: GAPOKTAN_QUERY_KEY({ ...params, type: 'anggota-gapoktan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<GapoktanAnggotaDataType>>
      >(`/kelembagaan/gapoktan/${params?.id_gapoktan}/anggota`, {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllAlsintanGapoktan = (
  params?: FilterGapoktanValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<AlsintanDataType>>> => ({
  queryKey: GAPOKTAN_QUERY_KEY({ ...params, type: 'alsintan-gapoktan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<AlsintanDataType>>
      >(`/kelembagaan/gapoktan/${params?.id_gapoktan}/alsintan`, {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllStrukturalGapoktan = (
  params?: FilterGapoktanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<GapoktanStrukturalDataType>>
> => ({
  queryKey: GAPOKTAN_QUERY_KEY({ ...params, type: 'struktural-gapoktan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<GapoktanStrukturalDataType>>
      >(`/kelembagaan/gapoktan/${params?.id_gapoktan}/struktural`, {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<GapoktanDataType>,
  MutationGapoktanValues
> => ({
  mutationFn: async (data: MutationGapoktanValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<GapoktanDataType>>(
        '/kelembagaan/gapoktan',
        {
          ...data,
          id_gapoktan: undefined,
          id_desa: data.id_desa.value,
          ada_sk: data.ada_sk === 'y'
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createAlsintan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<AlsintanDataType>,
  AddAlsintanValues
> => ({
  mutationFn: async (data: AddAlsintanValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<AlsintanDataType>>(
        `/kelembagaan/gapoktan/${data.id_gapoktan}/alsintan`,
        {
          ...data,
          alsintan: data.alsintan.map(komoditas => ({
            id: komoditas.id.value,
            jumlah: komoditas.jumlah ? Number(komoditas.jumlah) : undefined
          }))
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createAngotaGapoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PetaniDataType>,
  AddAnggotaGapoktanValues
> => ({
  mutationFn: async (data: AddAnggotaGapoktanValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<PetaniDataType>>(
        `/kelembagaan/gapoktan/${data.id_gapoktan}/anggota`,
        {
          ...data
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createStrukturalGapoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PetaniDataType>,
  AddStrukturalGapoktanValues
> => ({
  mutationFn: async (data: AddStrukturalGapoktanValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<PetaniDataType>>(
        `/kelembagaan/gapoktan/${data.id_gapoktan}/struktural`,
        {
          ...data
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<Omit<GapoktanDataType, 'poktan'>>,
  Omit<MutationGapoktanValues, 'poktan'>
> => ({
  mutationFn: async (data: Omit<MutationGapoktanValues, 'poktan'>) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<Omit<GapoktanDataType, 'poktan'>>
      >(`/kelembagaan/gapoktan/${data.id_gapoktan}`, {
        ...data,
        ...data,
        id_gapoktan: undefined,
        id_desa: data.id_desa.value,
        ada_sk: data.ada_sk === 'y'
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updateAlsintan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<AlsintanDataType>,
  AddAlsintanValues
> => ({
  mutationFn: async (data: AddAlsintanValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<AlsintanDataType>>(
        `/kelembagaan/gapoktan/${data?.id_gapoktan}/alsintan/${data?.alsintan_id}`,
        {
          ...data,
          // alsintan: data.alsintan.map(komoditas => ({
          //   id: komoditas.id.value,
          //   jumlah: komoditas.jumlah
          // })),
          jumlah: data.alsintan[0].jumlah
            ? Number(data.alsintan[0].jumlah)
            : undefined
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<GapoktanDataType>,
  { id_poktan: string }
> => ({
  mutationFn: async (data: { id_poktan: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<GapoktanDataType>
      >(`/kelembagaan/gapoktan/${data.id_poktan}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (params?: FilterGapoktanValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/kelembagaan/gapoktan/export`, { params })
      return response.data
    } catch (error) {
      console.error('Export error:', error)
      throw handleAxiosError(error)
    }
  }
})

const getRiwayatKodeGapoktan = (
  params: { id_gapoktan: string; page?: number; per_page?: number }
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<RiwayatKodeGapoktanDataType>>
> => ({
  queryKey: GAPOKTAN_QUERY_KEY({ ...params, type: 'riwayat-kode' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<RiwayatKodeGapoktanDataType>>
      >(`/kelembagaan/gapoktan/${params.id_gapoktan}/code-logs`, {
        params: {
          page: params.page,
          per_page: params.per_page
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteDataAlsintanGapoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<AlsintanDataType>,
  { id_gapoktan: string; id_data: string }
> => ({
  mutationFn: async (data: { id_gapoktan: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<AlsintanDataType>
      >(`/kelembagaan/gapoktan/${data?.id_gapoktan}/alsintan/${data?.id_data}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteDataAnggotaGapoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<GapoktanAnggotaDataType>,
  { id_gapoktan: string; id_data: string }
> => ({
  mutationFn: async (data: { id_gapoktan: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<GapoktanAnggotaDataType>
      >(`/kelembagaan/gapoktan/${data?.id_gapoktan}/anggota/${data?.id_data}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteDataStrukturalGapoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<GapoktanStrukturalDataType>,
  { id_gapoktan: string; id_data: string }
> => ({
  mutationFn: async (data: { id_gapoktan: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<GapoktanStrukturalDataType>
      >(
        `/kelembagaan/gapoktan/${data?.id_gapoktan}/struktural/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const daftarGapoktan = {
  queryKey: [GAPOKTAN_QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  deleteData,
  exportData,
  getAllAnggotaGapoktan,
  getAllAlsintanGapoktan,
  deleteDataAlsintanGapoktan,
  update,
  createAlsintan,
  updateAlsintan,
  deleteDataAnggotaGapoktan,
  createAngotaGapoktan,
  createStrukturalGapoktan,
  deleteDataStrukturalGapoktan,
  getAllStrukturalGapoktan,
  getRiwayatKodeGapoktan
}
